/**
 * Boxes 13 and 15 of the ICS-201: the safety message and the hazardous
 * materials summary.
 *
 * Built from the same records and editors the ICS-208 and ICS-208HM cards use,
 * rather than from the Initial Response Safety Analysis tab. The safety message
 * is the 208's record and the hazmat fields are the 208HM's, so what is entered
 * here shows on those cards once the incident escalates, and vice versa.
 *
 * Both APIs replace rather than patch, which is why each save resends the whole
 * record with only its own field changed: `toSafetyMessagePayload` for the
 * message, and the full form state for the hazmat write.
 */

import React from 'react';

import type { BaseSafetyMessage } from '../../../../../types/iapBaseTypes';
import { filterItemsByOperationalPeriod } from '../../../../../utils/operationalPeriodUtils';
import { useCurrentPeriod, usePeriods } from '../../../hooks/incidentHooks';
import { useCreateSafetyMessage } from '../../../hooks/useCreateSafetyMessage';
import { useSafetyMessages } from '../../../hooks/useSafetyMessages';
import { useUpdateSafetyMessage } from '../../../hooks/useUpdateSafetyMessage';
import {
  useGetHazmats,
  useGetIncidentInfo,
  usePostHazmats,
  useUpdateIncidentInfo,
} from '../../../hooks/useSafetyAnalysis';
import {
  HAZMAT_CLASSIFICATION,
  POTENTIAL_HAZARDS,
  REQUIRED_PPE,
  type HAZMATClassification,
  type PotentialHazard,
  type RequiredPPE,
} from '../../../initialResponsePage/SafetyAnalysis/types';
import { useRecordIcs201Version } from '../ics201VersionRecording';
import { IcsCheckboxGrid } from '../IcsCheckboxGrid';
import SaveableBox from '../SaveableBox';
import SaveableField from '../SaveableField';
import {
  environmentalHazardsOptions,
  otherHazardsOptions,
  physicalHazardsOptions,
} from '../SafetyPlan/safetyPlanOptions';
import {
  fromKnownHazardsDraft,
  toKnownHazardsDraft,
  toSafetyMessagePayload,
  type KnownHazardsDraft,
  type SafetyPlanPatch,
} from '../SafetyPlan/safetyPlanRecord';
import type { HazardousMaterialsFormState } from '../HazardousMaterials/HazardousMaterials';

const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="tw:text-sm tw:font-medium">{children}</span>
);

export const Ics201SafetyBox: React.FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const recordVersion = useRecordIcs201Version();
  const { data: safetyMessages } = useSafetyMessages();
  const { data: hazmatData } = useGetHazmats();
  const { data: incidentInfoData } = useGetIncidentInfo();
  const { data: periods } = usePeriods();
  const currentPeriod = useCurrentPeriod();

  const createSafetyMessage = useCreateSafetyMessage();
  const updateSafetyMessage = useUpdateSafetyMessage();

  const safetyMessage = React.useMemo(
    () =>
      filterItemsByOperationalPeriod(
        safetyMessages,
        currentPeriod.currentPeriod,
        periods,
        (message) => message.createdAt
      ),
    [safetyMessages, currentPeriod, periods]
  );

  /**
   * The hazmat card's whole state, so the two mutations below can be handed
   * everything with one field replaced. The fields this box does not show still
   * have to travel, or the 208HM loses them.
   */
  const hazmatFormState: HazardousMaterialsFormState = React.useMemo(
    () => ({
      hazmatClassification: hazmatData?.classifications ?? [],
      material: hazmatData?.material ?? [],
      potentialHazards: incidentInfoData?.potentialHazards ?? [],
      incidentNeeds: hazmatData?.incidentNeeds ?? [],
      additionalProductInfo: hazmatData?.additionalProductInfo ?? '',
      requiredPPE: incidentInfoData?.requiredPpe ?? [],
      notesOnPPE: incidentInfoData?.notesOnPpe ?? '',
      lelInstruments: hazmatData?.lelInstruments ?? '',
      o2Instruments: hazmatData?.o2Instruments ?? '',
      toxicityInstruments: hazmatData?.toxicityInstruments ?? '',
      radiologicalInstruments: hazmatData?.radiologicalInstruments ?? '',
      hazardMonitoringComments: hazmatData?.hazardMonitoringComments ?? '',
      standardOperatingProcedures: hazmatData?.sops ?? '',
      decontaminationProcedures: hazmatData?.decontaminationProcedure ?? '',
      medicalMonitoringRequired: hazmatData?.medicalMonitoring ?? false,
      medicalTreatmentAndTransportInPlace:
        hazmatData?.medicalTreatment ?? false,
      emergencyProcedures: hazmatData?.emergencyProcedures ?? '',
      otherClassification: null,
      airMonitoringRequired: null,
    }),
    [hazmatData, incidentInfoData]
  );

  const postHazmats = usePostHazmats(hazmatFormState);
  const updateIncidentInfo = useUpdateIncidentInfo(hazmatFormState);

  /** One box's edit to the safety message, sent as a whole record. */
  const saveMessage = React.useCallback(
    async (patch: SafetyPlanPatch) => {
      const payload: BaseSafetyMessage = toSafetyMessagePayload(
        safetyMessage,
        patch
      );
      if (safetyMessage) {
        await updateSafetyMessage.mutateAsync({
          id: safetyMessage.id,
          // The hook stamps the real incident id over whatever is passed; this
          // blank only satisfies the request type.
          data: { ...payload, incidentId: '' },
        });
      } else {
        await createSafetyMessage.mutateAsync(payload);
      }
      recordVersion();
    },
    [safetyMessage, updateSafetyMessage, createSafetyMessage, recordVersion]
  );

  /** One box's edit to the hazmat assessment, written to both records it spans. */
  const saveHazmat = React.useCallback(
    async (patch: Partial<HazardousMaterialsFormState>) => {
      const next = { ...hazmatFormState, ...patch };
      await postHazmats.mutateAsync(next);
      await updateIncidentInfo.mutateAsync(next);
      recordVersion();
    },
    [hazmatFormState, postHazmats, updateIncidentInfo, recordVersion]
  );

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-5">
      <SaveableField
        id="ics201-safety-message"
        label="Safety Message"
        multiline
        rows={4}
        maxLength={1000}
        placeholder="Enter the safety message..."
        disabled={disabled}
        value={safetyMessage?.safetyMessage ?? ''}
        onSave={(value) => void saveMessage({ safetyMessage: value })}
      />

      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <SubLabel>Known Hazards and Risks</SubLabel>
        <SaveableBox<KnownHazardsDraft>
          id="ics201-known-hazards"
          label="Known Hazards and Risks"
          disabled={disabled}
          value={toKnownHazardsDraft(safetyMessage)}
          onSave={(draft) =>
            void saveMessage({ knownHazards: fromKnownHazardsDraft(draft) })
          }
        >
          {(draft, setDraft) => (
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
              <IcsCheckboxGrid
                label="Physical Hazards"
                testId="ics201-physical-hazards"
                options={physicalHazardsOptions}
                value={draft.physicalHazards}
                disabled={disabled}
                onChange={(next) =>
                  setDraft({ ...draft, physicalHazards: next })
                }
              />
              <IcsCheckboxGrid
                label="Environmental Hazards"
                testId="ics201-environmental-hazards"
                options={environmentalHazardsOptions}
                value={draft.environmentalHazards}
                disabled={disabled}
                onChange={(next) =>
                  setDraft({ ...draft, environmentalHazards: next })
                }
              />
              <IcsCheckboxGrid
                label="Other Hazards"
                testId="ics201-other-hazards"
                options={otherHazardsOptions}
                value={draft.otherHazards}
                disabled={disabled}
                onChange={(next) => setDraft({ ...draft, otherHazards: next })}
              />
            </div>
          )}
        </SaveableBox>
      </div>

      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <SubLabel>Hazardous Materials Classification</SubLabel>
        <SaveableBox<HAZMATClassification[]>
          id="ics201-hazmat-classification"
          label="Hazardous Materials Classification"
          disabled={disabled}
          value={hazmatFormState.hazmatClassification}
          onSave={(next) => void saveHazmat({ hazmatClassification: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label="Hazardous Materials Classification"
              hideLabel
              testId="ics201-hazmat-classification-grid"
              columns={3}
              options={HAZMAT_CLASSIFICATION}
              value={draft}
              disabled={disabled}
              onChange={(next) => setDraft(next as HAZMATClassification[])}
            />
          )}
        </SaveableBox>
      </div>

      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <SubLabel>Potential Physical Effects</SubLabel>
        <SaveableBox<PotentialHazard[]>
          id="ics201-potential-effects"
          label="Potential Physical Effects"
          disabled={disabled}
          value={hazmatFormState.potentialHazards}
          onSave={(next) => void saveHazmat({ potentialHazards: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label="Potential Physical Effects"
              hideLabel
              testId="ics201-potential-effects-grid"
              // Each option is a full sentence, so they stack rather than tile.
              columns={2}
              options={POTENTIAL_HAZARDS}
              value={draft}
              disabled={disabled}
              onChange={(next) => setDraft(next as PotentialHazard[])}
            />
          )}
        </SaveableBox>
      </div>

      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <SubLabel>Required PPE</SubLabel>
        <SaveableBox<RequiredPPE[]>
          id="ics201-required-ppe"
          label="Required PPE"
          disabled={disabled}
          value={hazmatFormState.requiredPPE}
          onSave={(next) => void saveHazmat({ requiredPPE: next })}
        >
          {(draft, setDraft) => (
            <IcsCheckboxGrid
              label="Required PPE"
              hideLabel
              testId="ics201-required-ppe-grid"
              columns={4}
              options={REQUIRED_PPE}
              value={draft}
              disabled={disabled}
              onChange={(next) => setDraft(next as RequiredPPE[])}
            />
          )}
        </SaveableBox>
      </div>
    </div>
  );
};

export default Ics201SafetyBox;
